import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, BookmarkPlus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import * as filtrosApi from '@/api/filtros-guardados.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { rutaDePantalla } from '@/lib/ruta-de-pantalla';
import { Cargando } from '@/components/common/Cargando';

/**
 * A2 · FILTROS GUARDADOS — la misma barra en todas las pantallas con filtros.
 *
 * Es UN componente y no uno por pantalla a propósito: la auditoría A0 encontró
 * el mismo concepto implementado de tres formas distintas, y así es como
 * empieza. La pantalla solo dice *dónde* está (su ruta) y *qué hacer* cuando se
 * aplica un filtro guardado.
 *
 * ⚠️ LO QUE SE GUARDA ES EL `?query` DE LA URL, no las fechas resueltas. Eso es
 * posible porque A1 puso los filtros en la URL con tokens relativos, y los
 * resuelve al leer. Guardar "esta semana" guarda el token `rango=semana`: la
 * semana que viene sigue significando esta semana. Si se guardaran las fechas
 * calculadas, el filtro envejecería y el nombre seguiría mintiendo.
 *
 * ⚠️ LOS FAVORITOS SON MÍOS. La estrella fija el filtro como acceso rápido
 * **para quien la toca**, no para todo el equipo — nadie le llena la pantalla a
 * otro. Los favoritos se muestran como botones arriba; el resto vive en la
 * lista desplegable.
 *
 * ⚠️ LOS FILTROS SON COMPARTIDOS. Cualquiera los ve y los usa; renombrar y
 * borrar es del autor o de un administrador. Un filtro que otros usan todos los
 * días no puede desaparecer porque a alguien le pareció que sobraba. Por eso la
 * autoría se muestra siempre, y no solo cuando es de otro.
 */

export function FiltrosGuardados() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  // No `location.pathname` crudo: la lista de conversaciones vive dentro de
  // `/conversations/:id`, y sin recortar cada charla abierta seria "otra
  // pantalla" —el filtro guardado con una no aparecerian con otra, y el server
  // ademas rechazaria la ruta por no conocerla.
  const pantalla = rutaDePantalla(location.pathname);
  // El `?query` sale de la URL, no de una prop. Es lo que A1 dejó preparado: los
  // filtros YA viven ahí. Pedírselo a cada pantalla sería ocho oportunidades de
  // pasar algo distinto de lo que la URL dice.
  const consultaActual = location.search.replace(/^\?/, '');

  const [guardando, setGuardando] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [renombrando, setRenombrando] = useState<filtrosApi.FiltroGuardado | null>(null);
  const [porBorrar, setPorBorrar] = useState<filtrosApi.FiltroGuardado | null>(null);
  const [abierto, setAbierto] = useState(false);

  const lista = useQuery({
    queryKey: ['filtros-guardados', pantalla],
    queryFn: () => filtrosApi.listar(pantalla),
  });

  const refrescar = () => void qc.invalidateQueries({ queryKey: ['filtros-guardados', pantalla] });

  const crear = useMutation({
    mutationFn: () => filtrosApi.crear(pantalla, nombreNuevo, consultaActual),
    onSuccess: () => {
      toast.success(`Guardado como "${nombreNuevo.trim()}"`);
      setGuardando(false);
      setNombreNuevo('');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const renombrar = useMutation({
    mutationFn: (v: { id: string; nombre: string }) => filtrosApi.renombrar(v.id, v.nombre),
    onSuccess: () => {
      toast.success('Renombrado');
      setRenombrando(null);
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pisarConsulta = useMutation({
    mutationFn: (id: string) => filtrosApi.actualizarConsulta(id, consultaActual),
    onSuccess: () => {
      toast.success('Actualizado con los filtros de ahora');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => filtrosApi.borrar(id),
    onSuccess: () => {
      toast.success('Borrado');
      setPorBorrar(null);
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const favorito = useMutation({
    mutationFn: (id: string) => filtrosApi.alternarFavorito(id),
    onSuccess: refrescar,
    onError: (e: Error) => toast.error(e.message),
  });

  /** Aplica un filtro guardado: reemplaza el `?query` de la pantalla. */
  const aplicar = (f: filtrosApi.FiltroGuardado) => {
    // `replace` para no llenar el historial: ver el mismo criterio en
    // `useFiltrosUrl`, donde cambiar un filtro tampoco crea entrada.
    navigate(`${pantalla}${f.consulta ? `?${f.consulta}` : ''}`, { replace: true });
    setAbierto(false);
  };

  const filtros = lista.data?.filtros ?? [];
  const favoritos = filtros.filter((f) => f.esFavorito);
  const hayFiltrosPuestos = consultaActual.trim() !== '';

  /** ¿El filtro guardado es exactamente lo que hay puesto ahora? */
  const esElActual = (f: filtrosApi.FiltroGuardado) => f.consulta === consultaActual;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Los favoritos, como accesos rápidos. Solo los MÍOS. */}
      {favoritos.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => aplicar(f)}
          title={`${f.nombre} — lo guardó ${f.creadoPorNombre}`}
          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition ${
            esElActual(f)
              ? 'border-sky-400 bg-sky-50 font-medium text-sky-700 dark:bg-sky-950 dark:text-sky-300'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300'
          }`}
        >
          <Star className="h-3 w-3 fill-current" />
          {f.nombre}
        </button>
      ))}

      <div className="relative">
        <Button variant="secondary" size="sm" onClick={() => setAbierto((v) => !v)}>
          Filtros guardados{filtros.length > 0 ? ` (${filtros.length})` : ''}
        </Button>

        {abierto && (
          <>
            {/* Cerrar tocando afuera. Sin esto el panel queda abierto tapando
                la tabla y hay que volver al botón. */}
            <button
              type="button"
              aria-label="Cerrar"
              className="fixed inset-0 z-10 cursor-default"
              onClick={() => setAbierto(false)}
            />
            <div className="absolute left-0 z-20 mt-1 w-80 rounded-md border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-900">
              {lista.isLoading ? (
                <Cargando />
              ) : filtros.length === 0 ? (
                // Estado vacío que dice qué hacer, no solo "sin datos".
                <p className="px-2 py-3 text-xs text-slate-500">
                  Todavía no hay ninguno. Pon los filtros que usas seguido y toca{' '}
                  <strong>Guardar estos filtros</strong>: queda para todo el equipo.
                </p>
              ) : (
                <ul className="max-h-72 space-y-0.5 overflow-y-auto">
                  {filtros.map((f) => (
                    <li
                      key={f.id}
                      className="group flex items-center gap-1 rounded px-1 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <button
                        type="button"
                        onClick={() => favorito.mutate(f.id)}
                        title={
                          f.esFavorito
                            ? 'Sacar de mis accesos rápidos'
                            : 'Fijar como acceso rápido mío'
                        }
                        className="p-1 text-slate-400 hover:text-amber-500"
                      >
                        <Star
                          className={`h-3.5 w-3.5 ${f.esFavorito ? 'fill-amber-400 text-amber-400' : ''}`}
                        />
                      </button>

                      <button
                        type="button"
                        onClick={() => aplicar(f)}
                        className="min-w-0 flex-1 py-1.5 text-left"
                      >
                        <span
                          className={`block truncate text-sm ${
                            esElActual(f) ? 'font-semibold text-sky-700 dark:text-sky-300' : ''
                          }`}
                        >
                          {f.nombre}
                        </span>
                        {/* La autoría se muestra SIEMPRE. Es lo que permite
                            saber a quién pedirle un cambio en vez de chocar
                            con un "no tienes permiso". */}
                        <span className="block truncate text-[11px] text-slate-400">
                          {f.esMio ? 'tuyo' : `de ${f.creadoPorNombre}`}
                        </span>
                      </button>

                      {f.esMio && hayFiltrosPuestos && !esElActual(f) && (
                        <button
                          type="button"
                          onClick={() => pisarConsulta.mutate(f.id)}
                          title="Guardar acá los filtros que tienes puestos ahora"
                          className="p-1 text-slate-400 opacity-0 hover:text-sky-600 group-hover:opacity-100"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {f.esMio && (
                        <>
                          <button
                            type="button"
                            onClick={() => setRenombrando(f)}
                            title="Renombrar"
                            className="p-1 text-slate-400 opacity-0 hover:text-sky-600 group-hover:opacity-100"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPorBorrar(f)}
                            title="Borrar"
                            className="p-1 text-slate-400 opacity-0 hover:text-rose-600 group-hover:opacity-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {/* Solo se ofrece guardar si hay algo que guardar: "todo" ya existe, es la
          pantalla sin filtrar. */}
      {hayFiltrosPuestos && (
        <Button variant="ghost" size="sm" onClick={() => setGuardando(true)}>
          <BookmarkPlus className="mr-1 h-4 w-4" />
          Guardar estos filtros
        </Button>
      )}

      <Modal
        open={guardando}
        onClose={() => setGuardando(false)}
        title="Guardar estos filtros"
        footer={
          <>
            <Button variant="secondary" onClick={() => setGuardando(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => crear.mutate()}
              disabled={!nombreNuevo.trim() || crear.isPending}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Input
          label="¿Cómo lo llamamos?"
          value={nombreNuevo}
          autoFocus
          placeholder="Vencidos de esta semana"
          onChange={(e) => setNombreNuevo(e.target.value)}
        />
        <p className="mt-2 text-xs text-slate-500">
          Lo va a ver todo el equipo, con tu nombre al lado. Si el filtro incluye un período como
          &quot;esta semana&quot;, se guarda así: la semana que viene va a seguir mostrando esa
          semana, no esta.
        </p>
      </Modal>

      <Modal
        open={renombrando != null}
        onClose={() => setRenombrando(null)}
        title="Renombrar"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRenombrando(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                renombrando && renombrar.mutate({ id: renombrando.id, nombre: renombrando.nombre })
              }
              disabled={!renombrando?.nombre.trim() || renombrar.isPending}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Input
          label="Nombre"
          value={renombrando?.nombre ?? ''}
          autoFocus
          onChange={(e) => setRenombrando((v) => (v ? { ...v, nombre: e.target.value } : v))}
        />
      </Modal>

      <Modal
        open={porBorrar != null}
        onClose={() => setPorBorrar(null)}
        title="¿Borrar este filtro?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPorBorrar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => porBorrar && borrar.mutate(porBorrar.id)}
              disabled={borrar.isPending}
            >
              Borrar
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Se va a borrar <strong>{porBorrar?.nombre}</strong> para todo el equipo, no solo para ti.
          Si alguien lo tenía como acceso rápido, le va a desaparecer.
        </p>
      </Modal>
    </div>
  );
}
